if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item']
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.mediaGroupsEnabled = false;
        this.activeMediaGroup = 'product';
        this.allMediaItems = [];
        this.allThumbItems = [];
        this.mediaTabButtons = [];
        this.baseEnableDesktopSlider = false;
        this.baseEnableMobileThumbnails = false;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.baseEnableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.baseEnableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableDesktopSlider = this.baseEnableDesktopSlider;
        this.enableMobileThumbnails = this.baseEnableMobileThumbnails;
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;

        this.setupMediaGroups();
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      setupMediaGroups() {
        this.mediaTabButtons = [...this.querySelectorAll('[data-media-tab]')];
        if (!this.mediaTabButtons.length || !this.elements.mediaList) return;

        this.mediaGroupsEnabled = true;
        this.activeMediaGroup = this.dataset.activeMediaGroup || 'product';
        this.mediaListElement = this.elements.mediaList;
        this.thumbnailListElement = this.querySelector('[id^="Slider-Thumbnails"]');
        this.allMediaItems = [...this.mediaListElement.querySelectorAll('.product__media-item')];
        this.allThumbItems = this.thumbnailListElement
          ? [...this.thumbnailListElement.querySelectorAll('.product__thumbs-item')]
          : [];

        this.mediaTabButtons.forEach((button) => {
          button.addEventListener('click', () => {
            if (button.disabled) return;
            this.switchMediaGroup(button.dataset.mediaTab);
          });
        });

        this.mountMediaGroup(this.activeMediaGroup);
      }

      getGroupMedia(group) {
        if (!this.mediaGroupsEnabled) return this.elements.mediaItems || [];
        return this.allMediaItems.filter((item) => item.dataset.mediaGroup === group);
      }

      getGroupThumbs(group) {
        if (!this.mediaGroupsEnabled) return [];
        return this.allThumbItems.filter((item) => item.dataset.mediaGroup === group);
      }

      updateMediaTabs(group) {
        this.mediaTabButtons.forEach((button) => {
          const isActive = button.dataset.mediaTab === group;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-selected', isActive ? 'true' : 'false');
          button.tabIndex = isActive ? 0 : -1;
        });
      }

      mountMediaGroup(group) {
        if (!this.mediaGroupsEnabled) return;

        const mediaItems = this.getGroupMedia(group);
        const thumbItems = this.getGroupThumbs(group);

        if (!mediaItems.length) return;

        mediaItems.forEach((item) => {
          item.hidden = false;
        });
        thumbItems.forEach((item) => {
          item.hidden = false;
        });

        this.mediaListElement.replaceChildren(...mediaItems);
        if (this.thumbnailListElement) {
          this.thumbnailListElement.replaceChildren(...thumbItems);
        }

        this.elements.mediaItems = mediaItems;
        this.activeMediaGroup = group;
        this.dataset.activeMediaGroup = group;
        this.dataset.activeMediaCount = String(mediaItems.length);
        this.onlyImage = mediaItems.length < 2;
        this.enableDesktopSlider = this.baseEnableDesktopSlider && mediaItems.length > 1;
        this.enableMobileThumbnails = this.baseEnableMobileThumbnails && mediaItems.length > 1;

        this.updateMediaTabs(group);
      }

      switchMediaGroup(group) {
        if (!this.mediaGroupsEnabled || !group || group === this.activeMediaGroup) return;
        if (!this.getGroupMedia(group).length) return;

        window.pauseAllMedia(this);
        this.destroySlider();
        this.mountMediaGroup(group);
        this.setSliderOptions();
        this.updateMediaLayout();

        const status = this.querySelector('[id^="GalleryStatus"]');
        if (status) {
          status.textContent = `${group.charAt(0).toUpperCase()}${group.slice(1)} media selected`;
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap
            }
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev')
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction'
          },
          threshold: 2
        };

        this.thumbsOptions = {
          slidesPerView: 'auto',
          spaceBetween: 6,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap
            }
          }
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap
              }
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 'auto',
                spaceBetween: 8
              },
              1536: {
                slidesPerView: 'auto',
                spaceBetween: 8
              }
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }

        if (typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider.destroy();
          this.thumbsInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = this.mediaGroupsEnabled
          ? this.allMediaItems.filter((media) => !media.classList.contains('swiper-slide-duplicate'))
          : [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];

        if (allMedia) {
          allMedia.forEach((media) => {
            const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

            let source = {
              id: mediaIndex,
              mediaType: mediaType
            };

            switch (mediaType) {
              case 'model':
              case 'video':
              case 'external_video': {
                const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';
                const mediaElement = media.querySelector(htmlTag);

                if (mediaElement) {
                  source = {
                    ...source,
                    html: `<div class="pswp__item--${mediaType}">${mediaElement.outerHTML}</div>`
                  };
                }
                break;
              }
              default:
                source = {
                  ...source,
                  src: src,
                  width: pswpWidth,
                  height: pswpHeight
                };
                break;
            }

            dataSource.push(source);
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          returnFocus: false
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }) => {
          const targetMedia = this.mediaGroupsEnabled
            ? this.allMediaItems.find((item) => String(item.dataset.mediaIndex) === String(id))
            : this.querySelector(`.product__media-item[data-media-index="${id}"]`);
          return targetMedia?.querySelector('img') || thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);

          const currentData = this.lightbox?.pswp?.currSlide?.data;
          if (!currentData) return;

          const targetMedia = this.mediaGroupsEnabled
            ? this.allMediaItems.find((item) => String(item.dataset.mediaIndex) === String(currentData.id))
            : this.elements.mediaItems?.find((item) => String(item.dataset.mediaIndex) === String(currentData.id));

          if (!targetMedia) return;

          if (this.mediaGroupsEnabled && targetMedia.dataset.mediaGroup !== this.activeMediaGroup) {
            this.switchMediaGroup(targetMedia.dataset.mediaGroup);
          }

          const slider = this.sliderInstance?.slider;
          if (slider) {
            const localIndex = [...this.elements.mediaList.children].findIndex(
              (item) => String(item.dataset.mediaIndex) === String(currentData.id)
            );
            if (localIndex >= 0) {
              slider.slideTo(localIndex, 0, false);
            }
          }
        });

        this.lightbox.on('destroy', () => {
          const currentData = this.lightbox?.pswp?.currSlide?.data;
          const currentId = currentData?.id;
          if (currentId === undefined || currentId === null) return;

          const target = this.mediaGroupsEnabled
            ? this.allMediaItems.find((item) => String(item.dataset.mediaIndex) === String(currentId))
            : this.elements.mediaItems?.find((item) => String(item.dataset.mediaIndex) === String(currentId));

          if (!target) return;

          if (this.mediaGroupsEnabled && target.dataset.mediaGroup !== this.activeMediaGroup) {
            this.switchMediaGroup(target.dataset.mediaGroup);
          }

          if (!this.sliderInstance) {
            requestAnimationFrame(() => {
              const rect = target.getBoundingClientRect();
              const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
              if (!inView) {
                target.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            });
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: () => {
                this.lightbox?.pswp?.next();
              }
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: () => {
                this.lightbox?.pswp?.prev();
              }
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: () => {
              this.lightbox?.pswp?.close();
            }
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          }
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex } = swiper;
          const thumbsSwiper = swiper.thumbs?.swiper;

          if (thumbsSwiper) {
            thumbsSwiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance?.slider && this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image'
          };
        }

        if (!featuredMedia) return;

        if (this.mediaGroupsEnabled) {
          const targetMedia = this.allMediaItems.find((item) => item.dataset.mediaId == featuredMedia.id);
          if (targetMedia?.dataset.mediaGroup && targetMedia.dataset.mediaGroup !== this.activeMediaGroup) {
            this.switchMediaGroup(targetMedia.dataset.mediaGroup);
          }
        }

        if (this.sliderInstance?.slider) {
          const slideIndex = [...this.elements.mediaList.children].findIndex(
            (item) => item.dataset.mediaId == featuredMedia.id
          );
          if (slideIndex >= 0) {
            this.sliderInstance.slider.slideTo(slideIndex, 0, false);
          }
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image'
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = [...this.elements.mediaItems];

        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.replaceChildren(...newMedias);
        this.elements.mediaItems = newMedias;

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
